'use client';

import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/layout/navbar';
import { Footer } from '../components/layout/footer';
import { Search, Users, MessageSquare, ArrowRight, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCommunities } from '../hooks/use-communities';
import AddCommunityModal from '../components/modals/add-community-modal';

// No fallback data - will show empty state with option to create first community
const CATEGORY_FILTERS = [
  'All',
  'Tech',
  'Dev Tools',
  'Gaming',
  'AI',
  'Support',
  'Media',
  'Open Source',
  'Hardware',
  'Finance',
];

const formatNumber = (num) => {
  if (typeof num === 'string') {
    if (num.includes(',')) return num;
    num = parseInt(num.replace(/,/g, ''), 10);
  }
  if (typeof num !== 'number' || isNaN(num)) return '0';
  if (num >= 1000) {
    return (num / 1000).toFixed(num >= 10000 ? 0 : 1).replace(/\.0$/, '') + 'K';
  }
  return num.toLocaleString();
};

const CommunityCard = ({ item, index }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    whileInView={{ opacity: 1, scale: 1 }}
    transition={{ delay: index * 0.05 }}
    viewport={{ once: true }}
    className="group relative flex items-center justify-between p-6 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-violet-500/30 transition-all duration-300"
  >
    <div className="flex items-center gap-4">
      <div className="h-12 w-12 rounded-full bg-gradient-to-br from-violet-500 to-cyan-500 flex items-center justify-center text-lg font-bold">
        {item.name?.charAt(0) || 'C'}
      </div>
      <div>
        <h3 className="font-bold text-white group-hover:text-violet-300 transition-colors">
          {item.name}
        </h3>
        <p className="text-xs text-zinc-500">{item.category}</p>
      </div>
    </div>

    <div className="flex items-center gap-6">
      <div className="hidden md:block text-right">
        <div className="flex items-center gap-1.5 justify-end text-zinc-300">
          <Users className="w-4 h-4" />
          <span className="text-sm font-medium">
            {formatNumber(item.members || item.memberCount || 0)}
          </span>
        </div>
        <div className="flex items-center gap-1.5 justify-end text-zinc-500 mt-1">
          <MessageSquare className="w-3 h-3" />
          <span className="text-xs">
            {formatNumber(item.activeMembers || item.posts || 0)} Active
          </span>
        </div>
      </div>
      <Link to={item.slug ? `/forum/${item.slug}` : `/forum`}>
        <Button
          size="icon"
          variant="ghost"
          className="rounded-full hover:bg-violet-500/20 hover:text-violet-300"
        >
          <ArrowRight className="w-5 h-5" />
        </Button>
      </Link>
    </div>
  </motion.div>
);

const CommunityCardSkeleton = ({ index }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ delay: index * 0.05 }}
    className="flex items-center justify-between p-6 rounded-2xl bg-white/5 border border-white/10"
  >
    <div className="flex items-center gap-4">
      <div className="h-12 w-12 rounded-full bg-zinc-800 animate-pulse" />
      <div className="space-y-2">
        <div className="h-4 w-32 bg-zinc-800 rounded animate-pulse" />
        <div className="h-3 w-20 bg-zinc-800 rounded animate-pulse" />
      </div>
    </div>
    <div className="hidden md:flex items-center gap-6">
      <div className="space-y-2 text-right">
        <div className="h-4 w-16 bg-zinc-800 rounded animate-pulse ml-auto" />
        <div className="h-3 w-12 bg-zinc-800 rounded animate-pulse ml-auto" />
      </div>
      <div className="h-10 w-10 rounded-full bg-zinc-800 animate-pulse" />
    </div>
  </motion.div>
);

export default function DiscoverPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [showAddModal, setShowAddModal] = useState(false);

  const { useGetAllCommunities } = useCommunities();
  const {
    data: communities,
    isLoading,
    isError,
  } = useGetAllCommunities({
    category: activeCategory !== 'All' ? activeCategory : undefined,
    search: searchTerm || undefined,
  });

  // Use fetched communities - no fallback, show empty state instead
  const displayCommunities = useMemo(() => {
    if (isLoading) return [];
    if (!communities || communities.length === 0) {
      return [];
    }
    return communities;
  }, [communities, isLoading]);

  return (
    <div className="flex min-h-screen flex-col bg-black text-white selection:bg-violet-500/30">
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-[-20%] left-[20%] w-[60%] h-[60%] rounded-full bg-violet-900/20 blur-[150px]" />
      </div>

      <Navbar />

      <main className="flex-1 relative z-10 pt-24 pb-16">
        <section className="container px-4 md:px-6 mb-16">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8 mb-12">
            <div>
              <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-4">
                Discover{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-cyan-400">
                  Communities
                </span>
              </h1>
              <p className="text-zinc-400 text-lg max-w-xl">
                Find your tribe. Explore active Paranets and communities powered
                by Talktrail.
              </p>
            </div>

            <div className="w-full md:w-auto flex flex-col sm:flex-row gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <input
                  type="text"
                  placeholder="Search communities..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full sm:w-[300px] h-12 pl-10 pr-4 rounded-full bg-white/5 border border-white/10 text-white placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50 transition-all"
                />
              </div>
              <Button
                className="rounded-full bg-white text-black hover:bg-zinc-200 h-12 px-6"
                onClick={() => setShowAddModal(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add your community
              </Button>
            </div>
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap gap-2 mb-12">
            {CATEGORY_FILTERS.map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveCategory(filter)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeCategory === filter
                    ? 'bg-violet-600 text-white shadow-lg shadow-violet-500/25'
                    : 'bg-white/5 text-zinc-400 hover:bg-white/10 hover:text-white'
                }`}
              >
                {filter}
              </button>
            ))}
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {Array.from({ length: 6 }).map((_, idx) => (
                <CommunityCardSkeleton key={idx} index={idx} />
              ))}
            </div>
          )}

          {/* Error State */}
          {isError && !isLoading && (
            <div className="text-center py-16">
              <p className="text-zinc-400 mb-4">Failed to load communities</p>
              <Button
                variant="outline"
                onClick={() => window.location.reload()}
                className="rounded-full"
              >
                Try Again
              </Button>
            </div>
          )}

          {/* Communities Grid */}
          {!isLoading && !isError && (
            <>
              {displayCommunities.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {displayCommunities.map((comm, idx) => (
                    <CommunityCard
                      key={comm._id || comm.slug || idx}
                      item={comm}
                      index={idx}
                    />
                  ))}
                </div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center py-20 px-4"
                >
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-violet-500/10 border border-violet-500/20 mb-6">
                    <Users className="w-10 h-10 text-violet-400" />
                  </div>
                  <h2 className="text-2xl font-bold mb-3">
                    No Communities Yet
                  </h2>
                  <p className="text-zinc-400 mb-6 max-w-md mx-auto">
                    {searchTerm
                      ? `No results for "${searchTerm}"`
                      : activeCategory !== 'All'
                      ? `No communities in ${activeCategory} category yet. Be the first to create one!`
                      : 'Be the first to create a community and start building your tribe!'}
                  </p>
                  <Button
                    className="rounded-full bg-violet-600 hover:bg-violet-700 text-white h-12 px-8"
                    onClick={() => setShowAddModal(true)}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create First Community
                  </Button>
                </motion.div>
              )}
            </>
          )}

          {/* Stats footer */}
          {!isLoading && displayCommunities.length > 0 && (
            <div className="mt-12 text-center">
              <p className="text-zinc-500 text-sm">
                Showing {displayCommunities.length}{' '}
                {displayCommunities.length === 1 ? 'community' : 'communities'}
                {activeCategory !== 'All' && ` in ${activeCategory}`}
              </p>
            </div>
          )}
        </section>
      </main>

      <Footer />

      {/* Add Community Modal */}
      <AddCommunityModal open={showAddModal} onOpenChange={setShowAddModal} />
    </div>
  );
}
